import { ReactElement, useEffect, useState } from 'react';
import { FormControl, Autocomplete as MuiAutocomplete, AutocompleteProps as MuiAutocompleteProps } from '@mui/material';
import { CaretDown } from '@assets/svgs';
import * as styles from '@components/autocomplete/Autocomplete.styles';
import ConditionalWrapper from '@utils/ConditionalWrapper';
import { AutocompletePaper } from '@components/autocomplete/paper/AutocompletePaper';

export const Autocomplete = <T,>(
  props: MuiAutocompleteProps<T, boolean | undefined, boolean | undefined, boolean | undefined> & {
    isQuotePanel?: boolean;
    /** This is specifically needed to prevent auto-selection when typing matches an option exactly (which triggers on change twice, which if your on change is a POST call is bad....)
     * NOTE: long term would be a better fix, BUT we have 33 instances of Autocomplete that could potentially be impacted, this is a safer quick fix to prevent unintended consequences in the short term.
     */
    disableAutoSelectOnTypeMatch?: boolean;
  },
) => {
  const {
    value,
    sx,
    disabled,
    fullWidth,
    isQuotePanel = false,
    disableAutoSelectOnTypeMatch = false,
    ...other
  } = props;
  const [open, setOpen] = useState(false);
  const [matchedValue, setMatchedValue] = useState<string | undefined>(undefined);

  const wrapperComponent = (children: ReactElement) => (
    <FormControl sx={{ height: '100%', justifyContent: 'flex-end', ...(fullWidth ? { width: '100%' } : {}) }}>
      {children}
    </FormControl>
  );

  const handleOnInputChange = (event: any, newValue: string, reason: any) => {
    if (!open) {
      return;
    }

    if (other.multiple) {
      other?.onInputChange?.(event, newValue, reason);
      return;
    }

    const typedValue = newValue?.toLowerCase();

    const filteredOptions: any[] = other.options?.filter((option: any) =>
      option.label?.toLowerCase().startsWith(typedValue),
    );

    other?.onInputChange?.(event, newValue, reason);

    if (
      !disableAutoSelectOnTypeMatch &&
      filteredOptions.length === 1 &&
      filteredOptions[0].label.toLowerCase() === typedValue
    ) {
      if (matchedValue !== typedValue) {
        other?.onChange?.(event, filteredOptions[0], reason);
        setOpen(false);
        // matched value helps keep track of the value that was selected if user is spamming keyboard strokes
        setMatchedValue(typedValue);
      }
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setMatchedValue(undefined);
  }, [open]);

  return (
    <ConditionalWrapper condition={isQuotePanel} wrapper={wrapperComponent}>
      <MuiAutocomplete
        data-testid='autocomplete-field'
        slots={{
          paper: AutocompletePaper,
        }}
        {...other}
        open={open}
        onOpen={(e) => {
          setOpen(true);
          other?.onOpen?.(e);
        }}
        onClose={(e, reason) => {
          setOpen(false);
          other?.onClose?.(e, reason);
        }}
        options={other.options ?? []}
        onInputChange={handleOnInputChange}
        popupIcon={<CaretDown className='MuiAutocomplete-icon' />}
        value={value}
        disabled={disabled}
        fullWidth={fullWidth}
        sx={{
          ...styles.baseStyles,
          ...styles.endAdornment,
          ...(disabled ? styles.isDisabled : {}),
          ...sx,
        }}
        slotProps={{
          listbox: { role: 'listbox', ...other.slotProps?.listbox },
        }}
      />
    </ConditionalWrapper>
  );
};
